<template>
  <div>
    <section>
      <h3>Upload URLs via spreadsheet</h3>
      <div
        class="d-flex justify-content-center"
      >
        <b-form-group
          :state="isValidFile"
          label-for="spreadsheet-urls-upload-box"
        >
          <template v-slot:label>
            <base-help-tooltip
              class="gutters-0 new-gutters-xs"
            >
              <template
                v-slot:left
                class="gutters-0 new-gutters-xs test?"
              >
                <p class="mb-0">
                  Choose a file to upload
                </p>
              </template>
              <template v-slot:popover-body>
                <p class="mb-0">
                  Accepted file formats are ".xls" or ".xlsx"
                </p>
              </template>
            </base-help-tooltip>
          </template>
          <b-form-file
            id="spreadsheet-urls-upload-box"
            v-model="URLsSpreadsheetFile"
            :accept="acceptedFileTypes.join()"
            :class="['is-draggable', {'is-processed': URLsSpreadsheetFile}]"
            :disabled="!canUploadURLsSpreadsheet"
            :state="isValidFile"
            data-test="spreadsheet-urls-upload-box"
          >
            <template v-slot:file-name>
              <p
                v-if="isValidFile"
                class="mb-0"
              >
                {{ URLsSpreadsheetFile.name }}
              </p>
              <p
                v-else
                class="mb-0"
              >
                File format not supported
              </p>
            </template>
          </b-form-file>
        </b-form-group>
      </div>
    </section>
    <hr class="my-1">
    <section class="p-5">
      <h3>Or, manually input URLs</h3>
      <b-row
        v-for="(url, index) in urlsListWithBlank"
        :key="index"
        class="gutters-0 new-gutters-xs"
      >
        <b-col
          class="mb-2"
          lg="12"
        >
          <div
            class="row gutters-0 new-gutters-xs"
          >
            <div class="col">
              <div class="input">
                <span class="input-icon-addon">
                  {{ index + 1 }}
                </span>
                <b-form-input
                  :data-test="`content-item-url-upload-input-${index}`"
                  :disabled="!canUploadURLsManually"
                  :state="isValidURL(url)"
                  :value="url"
                  class="form-control"
                  pattern="https?://.+"
                  placeholder="Enter a URL"
                  type="url"
                  @input="updateURLInput($event, index)"
                  @paste.prevent="pasteManualURLs($event, index)"
                />
                <b-form-invalid-feedback
                  id="input-live-feedback"
                  data-test="content-item-url-upload-input-invalid-feedback"
                >
                  Please enter a valid Url
                </b-form-invalid-feedback>
              </div>
            </div>
            <span
              class="col-auto"
            >
              <b-button
                :disabled="isDeleteManualURLDisabled(index)"
                variant="danger"
                @click="deleteManualURL(index)"
              >
                <i class="fa-light fa-trash-can" />
              </b-button>
            </span>
          </div>
        </b-col>
      </b-row>
    </section>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';

import helper from '@/core/helper';
import urlHelper from '@/core/mixins/urlHelper';
import BaseHelpTooltip from '@/components/BaseHelpTooltip.vue';

function initialData() {
  return {
    uploadedFile: null,
    URLsSpreadsheetFile: null,
    manualURLsList: [],
  };
}

export default {
  name: 'UploadUrlsTab',
  components: { BaseHelpTooltip },
  mixins: [
    urlHelper,
  ],
  data() {
    return initialData();
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
    ]),
    ...mapState('content', [
      'scrapingBatchId',
      'feedId',
    ]),
    ...mapState('content', {
      scrapedContent: 'newScrapedContent',
    }),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_NEW_CAMPAIGN,
    ]),
    isTabTouched() {
      return this.manualURLsList.length > 0 || this.URLsSpreadsheetFile != null;
    },
    isUploadValid() {
      if (this.manualURLsList.length && this.canUploadURLsManually) return this.areAllURLsValid;
      if (this.URLsSpreadsheetFile && this.canUploadURLsSpreadsheet) return this.isValidFile;
      return false;
    },
    canUploadURLsSpreadsheet() {
      return !this.manualURLsList.length;
    },
    canUploadURLsManually() {
      return !this.URLsSpreadsheetFile;
    },
    acceptedFileTypes() {
      return [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
      ];
    },
    isValidFile() {
      if (!this.URLsSpreadsheetFile) return null;
      return this.URLsSpreadsheetFile?.type
          && this.acceptedFileTypes.includes(this.URLsSpreadsheetFile.type);
    },
    urlsListWithBlank() {
      return this.manualURLsList.concat('');
    },
    areAllURLsValid() {
      if (this.manualURLsList.length < 1) return false;
      const filteredURLs = this.manualURLsList.filter((url) => url);
      return filteredURLs.length
          && filteredURLs.every((url) => this.isValidURL(url));
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.UPLOAD_URLS_SPREADSHEET_FOR_SCRAPE,
      contentTypes.SCRAPE_CONTENT,
      contentTypes.RESCRAPE_CONTENT,
      contentTypes.SET_CONTENT_ITEMS_URLS_CHANGED,
    ]),
    updateURLInput: helper.debounce(function updateURLInput(value, index) {
      if (index === this.urlsListWithBlank.length - 1) {
        this.manualURLsList.push('');
      }
      this.manualURLsList.splice(index, 1, value);
    }, 500),
    isValidURL(str) {
      if (!str) return;
      return helper.validURL(this.removeUrlTrailingSlash(str));
    },
    pasteManualURLs(event, index) {
      const clipboardData = event.clipboardData.getData('text')
        .split(/\r?\n/)
        .filter((el) => el !== '');
      if (clipboardData.length === 0) return;
      const pasteList = [];
      for (let i = 0; i < clipboardData.length; i++) {
        pasteList.push(clipboardData[i]);
      }
      this.manualURLsList.splice(index, pasteList.length, ...pasteList);
    },
    deleteManualURL(index) {
      this.manualURLsList.splice(index, 1);
    },
    isDeleteManualURLDisabled(index) {
      return index === this.urlsListWithBlank.length - 1 || !this.canUploadURLsManually;
    },
    launchFilePicker() {
      this.$refs.file.click();
    },
    async uploadURLsSpreadsheet(file) {
      if (!this.isUploadValid) return;
      this.SET_CONTENT_ITEMS_URLS_CHANGED(true);
      try {
        await this.UPLOAD_URLS_SPREADSHEET_FOR_SCRAPE({
          advertiserId: this.campaign.advertiserId,
          feedId: this.feedId,
          campaignId: this.campaign.id,
          file,
        });
        this.$emit('closeModal', { fetchArticles: true });
      } catch (error) {
        this.$emit('uploadIsFailed');
      }
    },
    async uploadUrlsManually(URLs) {
      if (!this.isUploadValid) return;
      const scrapingPayload = {
        advertisersId: this.campaign.advertiserId,
        feedId: this.feedId,
        campaignId: this.campaign.id,
        urls: this.scrapedContent.map((contentItem) => contentItem.url)
          .concat([...new Set(URLs)]),
      };
      this.SET_CONTENT_ITEMS_URLS_CHANGED(true);
      if (this.scrapingBatchId === 0) {
        await this.SCRAPE_CONTENT(scrapingPayload);
      } else {
        scrapingPayload.contentScrapeId = this.scrapingBatchId;
        await this.RESCRAPE_CONTENT(scrapingPayload);
      }
      this.$emit('closeModal', { fetchArticles: true });
    },
    upload() {
      if (this.GET_IS_NEW_CAMPAIGN) return;
      this.isUploadButtonLoading = true;
      const URLs = this.manualURLsList.filter((url) => url)
        .map((url) => this.removeUrlTrailingSlash(url));
      if (URLs.length) return this.uploadUrlsManually(URLs);
      if (this.URLsSpreadsheetFile) return this.uploadURLsSpreadsheet(this.URLsSpreadsheetFile);
    },
    resetData() {
      Object.assign(this.$data, initialData());
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .modal-header {
    border-bottom: 0;
  }
}
.input-icon-addon {
  max-height: 38px;
}
.form-control {
  background-image: unset;
}
.nav-tabs {
  height: 72px;
  margin: 0;
  position: relative !important;
}
.nav-item {
  width: 50%;
  height: 42px;
  padding: 0;
  position: absolute !important;
  bottom: 0;
  left: 0;
}
.full-width {
  width: 100% !important;
}
.nav-item:nth-child(2) {
  left: 50%;
}
.nav-link {
  height: 40px;
  width: 100%;
  border-radius: 2px 2px 0 0;
  position: absolute;
  bottom: 0;
  left: 0;

  &.active,
  &:focus {
    height: 48px;
    border-radius: 2px 2px 0 0;
    outline: none;
  }
}
.upload-files-header {
  display: inline-block;
}
.popover-body {
  padding: 0;
}
</style>
